// @ts-nocheck
/**
 * RendererRegistry.ts — Template V2 M3 Renderer Registry (WP-017)
 *
 * Maintains a typed map of M3RenderableType → ComponentRenderer.
 * dispatch() assembles a ComponentRenderContext and delegates to the
 * registered renderer, falling back to FALLBACK_RENDERER for unknown types.
 *
 * DEC-017: registry lives at runtime/renderer/.
 */

import { FALLBACK_RENDERER } from './fallbackRenderer';
import { resolveStyleBlock } from './rendererUtils';
import type {
    ChildRenderFunction,
    ComponentRenderContext,
    ComponentRenderer,
    M3RenderableType,
    NodeRenderInput,
    RenderDescriptor,
} from './types';

export class RendererRegistry {
  private readonly renderers = new Map<M3RenderableType, ComponentRenderer<unknown>>();

  /**
   * Registers a renderer for its declared supportedType.
   * Throws if a renderer for that type is already registered (fail-fast in dev
   * to surface accidental double-registration early).
   */
  register<T extends M3RenderableType>(
    renderer: ComponentRenderer<unknown>,
  ): void {
    if (this.renderers.has(renderer.supportedType)) {
      throw new Error(
        `[RendererRegistry] Duplicate registration for type "${renderer.supportedType}". ` +
          'Each component type may only have one renderer.',
      );
    }
    this.renderers.set(renderer.supportedType, renderer);
  }

  /**
   * Dispatches a node through its registered renderer.
   * Falls back to FALLBACK_RENDERER (visible=false, emits diagnostic) for any
   * type without a registered renderer.
   */
  dispatch(input: NodeRenderInput, childRenderer: ChildRenderFunction): RenderDescriptor {
    const type = input.node.type as M3RenderableType;
    const renderer = this.renderers.get(type) ?? FALLBACK_RENDERER;

    const context: ComponentRenderContext<unknown> = {
      node: input.node,
      resolvedProps: input.node.props,
      resolvedBindingValue: input.resolvedBindingValue,
      visible: input.visible,
      enabled: input.enabled,
      childRenderer,
      expressionEngine: input.expressionEngine ?? null,
      bindingContext: input.bindingContext,
      issues: input.issues,
      compiledTemplate: input.compiledTemplate,
      assetContext: input.assetContext,
      locale: input.locale,
    };

    const descriptor = renderer.render(context);
    const chrome = resolveStyleBlock(input.node.style, context);
    if (chrome) {
      descriptor.chrome = chrome;
    }
    return descriptor;
  }

  /** Returns true if a renderer has been registered for the given type. */
  isRegistered(type: M3RenderableType): boolean {
    return this.renderers.has(type);
  }

  /** Returns the set of currently registered component types. */
  registeredTypes(): ReadonlySet<M3RenderableType> {
    return new Set(this.renderers.keys());
  }
}
